import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Webcam from "react-webcam";

import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { RecognitionStatus } from "types/imageRecognition";
import { useStyles } from "./CameraScan.styles";
import { globalColors } from "assets/colors-global";
import { useTranslation } from "react-i18next";
import { rotateBase64Image } from "utils/dom.util";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";

const statusColor: Record<RecognitionStatus, string> = {
  ERROR: globalColors.error.main,
  PROGRESS: globalColors.warning.main,
  SUCCESS: globalColors.success.main,
};

const getWindowDimensions = (windowDimensions: any = {}) => {
  const { innerWidth: width, innerHeight: height } = window;

  const orientationType =
    document.body.scrollHeight < document.body.scrollWidth
      ? "landscape-primary"
      : "portrait-primary";

  if (
    orientationType === "landscape-primary" &&
    windowDimensions.orientation === "portrait-primary"
  ) {
    return {
      width: windowDimensions.height,
      height: windowDimensions.width,
      orientation: orientationType,
    };
  }
  return {
    width,
    height,
    orientation: orientationType,
  };
};
interface CameraScanProps {
  onImageCapture?: (
    data: { img: HTMLImageElement; index: number }
  ) => void | Promise<void>;
  onClose?: () => void;
  status: RecognitionStatus;
  message: string;
  captureEnabled?: boolean;
  showNotSureOverlay?: boolean;
  notSureMessage?: string;
  postFramesToNative?: boolean;
}

const CameraScan = ({
  onImageCapture,
  onClose,
  status,
  message,
  captureEnabled = true,
  showNotSureOverlay = false,
  notSureMessage,
  postFramesToNative = false,
}: CameraScanProps) => {
  const [cameraLoaded, setCameraLoaded] = useState(false);
  const { classes } = useStyles();
  const windowDimensionsRef = useRef<any>(null);
  // const imageCounterRef = useRef<number>(50);
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );
  const { t } = useTranslation();
  const webcamRef = useRef<Webcam>(null);
  const userMediaReadyRef = useRef<boolean>(false);
  const loadedDataReadyRef = useRef<boolean>(false);
  const captureIntervalRef = useRef<number | null>(null);
  const errorCountRef = useRef<number>(0);
  const currentCaptureRateRef = useRef<number>(250);
  const isFrameInProcessRef = useRef<boolean>(false);

  useEffect(() => {
    windowDimensionsRef.current = windowDimensions;
  }, [windowDimensions]);

  /**
   * Adjust capture rate based on error count and device capabilities
   */
  const adjustCaptureRate = useCallback((hasError: boolean) => {
    if (hasError) {
      errorCountRef.current += 1;
      // Increase interval (slow down) when errors occur
      if (errorCountRef.current >= 3) {
        currentCaptureRateRef.current = Math.min(currentCaptureRateRef.current * 1.5, 500); // Max 500ms
        errorCountRef.current = 0; // Reset after adjustment
      }
    } else {
      // Gradually return to normal rate on success
      if (errorCountRef.current > 0) {
        errorCountRef.current = Math.max(0, errorCountRef.current - 1);
      }
      if (currentCaptureRateRef.current > 100) {
        currentCaptureRateRef.current = Math.max(250, currentCaptureRateRef.current * 0.9);
      }
    }

    const adjustedRate = Math.max(250, currentCaptureRateRef.current);
    
    return adjustedRate;
  }, []);

  const capture = useCallback(async () => {
    if (!captureEnabled) {
      return;
    }
    if (!webcamRef.current?.getScreenshot) {
      return;
    }
    if (isFrameInProcessRef.current) {
      return;
    }

    const { width = 0, height = 0 } =
      webcamRef.current?.getCanvas()?.getContext("2d")?.canvas || {};
    if (width === 0 || height === 0) return;
    // console.log("Image canvas: ", {width, height})
    // const isLandscape = width > height;

    const image = new Image();
    let imageSrc: string;
    
    try {
      const screenshot = webcamRef.current.getScreenshot();
      if (!screenshot) {
        return;
      }
      if (windowDimensionsRef.current.orientation === "landscape-primary") {
        imageSrc = await rotateBase64Image(screenshot);
      } else {
        imageSrc = screenshot;
      }
      isFrameInProcessRef.current = true;
      image.src = imageSrc;
      const index = Date.now();
      if (postFramesToNative && status === "SUCCESS") {
        const dataForNativeApp = {
          image: imageSrc,
          index,
        };
        window.postMessage(dataForNativeApp);
      }
      
      image.onload = async () => {
        const data: { img: HTMLImageElement; index: number } = {
          img: image,
          index,
        };
        try {
          await onImageCapture?.(data);
        } finally {
          image.src = "";
          image.onload = null;
          image.onerror = null;
          isFrameInProcessRef.current = false;
        }
      };
      
      image.onerror = () => {
        image.src = "";
        image.onload = null;
        image.onerror = null;
        isFrameInProcessRef.current = false;
      };
    } catch (error) {
      image.src = "";
      image.onload = null;
      image.onerror = null;
      isFrameInProcessRef.current = false;
      console.error("Error capturing image:", error);
    }
  }, [captureEnabled, onImageCapture, postFramesToNative, status]);

  useEffect(() => {
    if (captureEnabled) {
      // Use adaptive capture rate
      currentCaptureRateRef.current = 300;
      
      const startCapture = () => {
        if (!captureEnabled) {
          return;
        }
        capture();
        const rate = adjustCaptureRate(status === "ERROR");
        captureIntervalRef.current = window.setTimeout(() => {
          startCapture();
        }, rate);
      };
      
      startCapture();
      
      return () => {
        if (captureIntervalRef.current !== null) {
          clearTimeout(captureIntervalRef.current);
          captureIntervalRef.current = null;
        }
      };
    } else {
      if (captureIntervalRef.current !== null) {
        clearTimeout(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
    }
  }, [capture, captureEnabled, adjustCaptureRate, status]);

  useEffect(() => {
    function handleResize() {
      const { width, height, orientation } =
        getWindowDimensions(windowDimensions);

      const res = { width, height, orientation };
      setWindowDimensions(res);
    }

    window.addEventListener("resize", handleResize);
    return () => {window.removeEventListener("resize", handleResize);
      onClose?.();
    }
  }, []);

  // Re-acquire camera when screen turns back on (stream dies when screen is off)
  // const [cameraKey, setCameraKey] = useState(0);
  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === "visible") {
  //       // Force react-webcam to remount and re-acquire the camera stream
  //       setCameraKey((prev) => prev + 1);
  //     }
  //   };
  //   document.addEventListener("visibilitychange", handleVisibilityChange);
  //   return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  // }, []);

  const checkCameraReady = useCallback(() => {
    // Only check once during initialization - skip if camera is already loaded
    if (cameraLoaded) {
      return;
    }
    
    // This ensures the camera stream is fully initialized before starting captures
    if (userMediaReadyRef.current && loadedDataReadyRef.current) {
        // This prevents memory pressure during camera initialization
        setTimeout(() => {
          setCameraLoaded(true);
        }, 500);
      } else {
        setCameraLoaded(true);
    }
  }, [cameraLoaded]);

  const onUserMedia = useCallback(() => {
    // Called when user media stream is successfully obtained
    userMediaReadyRef.current = true;
    checkCameraReady();
  }, [checkCameraReady]);

  const onCameraLoaded = useCallback(() => {
    // Called when video element has loaded its first frame
    loadedDataReadyRef.current = true;
    checkCameraReady();
  }, [checkCameraReady]);

  const handleUserMediaError = useCallback((error: string | DOMException) => {
    if (showNotSureOverlay) {
      return; // Don't close on media error when NOT_SURE overlay is active
    }
    if (onClose) {
      onClose();
    }
  }, [onClose, showNotSureOverlay]);

  const videoConstraints = useMemo(
    () => ({
      width: { ideal: 720 },
      height: { ideal: 720 },
      facingMode: { exact: "environment" },
      frameRate: { max: 12 },
    }),
    [windowDimensions]
  );

  return (
    <>
      <div className={classes.webcamHeader}>
        <div className={classes.webcamProgress}>
          <LinearProgress
            color="primary"
            sx={(theme) => ({
              backgroundColor: theme.palette.transparent.main,
              height: "1px",
              "& .MuiLinearProgress-bar": {
                backgroundColor: theme.palette.transparent.main,
                height: "1px",
              },
            })}
          />
        </div>
        <CloseSharpIcon onClick={onClose} />
      </div>
      <Webcam
        // key={cameraKey}
        className={classes.webcam}
        audio={false}
        height={windowDimensions.height}
        forceScreenshotSourceSize={true}
        screenshotQuality={0.7}
        width={windowDimensions.width}
        ref={webcamRef}
        onUserMedia={onUserMedia}
        onLoadedData={onCameraLoaded}
        allowFullScreen
        screenshotFormat="image/jpeg"
        videoConstraints={videoConstraints}
        onUserMediaError={handleUserMediaError}
      />
      <div className={classes.wrapper}>
        <div className={classes.wrapperContent}>
          <div
            className={classes.borderedHole}
            style={{ borderColor: statusColor[status] }}
          >
            {!cameraLoaded && (
              <div className={classes.holeLoadingOverlay}>
                <CircularProgress size={48} sx={{ color: "white" }} />
              </div>
            )}
          </div>
          {message && status === "ERROR" && cameraLoaded ? (
            <div className={classes.tooltipWrapper}>
              <div className={classes.messageTooltip}>{t(message)}</div>
            </div>
          ) : null}
          {showNotSureOverlay && notSureMessage ? (
              <div className={classes.tooltipWrapper}>
                <div className={classes.messageTooltip}>{t(notSureMessage)}</div>
              </div>
            ) : null}
        </div>
      </div>
    </>
  );
};

export default CameraScan;
